import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Chip,
  Divider,
} from '@mui/material';
import { Upload, CloudUpload, Check, Error as ErrorIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../services/api';
import type { ImportMondaySalesRow, ImportMondaySalesResult } from '../types';

interface CsvRow {
  [key: string]: string;
}

// Columnas exactas de Monday.com para Ventas (29 columnas)
interface ColumnMapping {
  nombre?: string;
  tareas?: string;
  responsable?: string;
  tipoDeOpps?: string;
  cliente?: string;
  etapas?: string;
  valorDelAcuerdo?: string;
  facturaFinal?: string;
  probabilidad?: string;
  ofertaFacturaRemito?: string;
  comprobante?: string;
  fechaDeCierre?: string;
  remitoDeEntrega?: string;
  condicionDelivery?: string;
  estadoDelivery?: string;
  recibido?: string;
  infoEntrega?: string;
  pago?: string;
  condicionPago?: string;
  conciliado?: string;
  abono?: string;
  saldo?: string;
  fechaDeEntrega?: string;
  fechaVto?: string;
  fechaAbono?: string;
  diasDeAtraso?: string;
  comision?: string;
  montoComision?: string;
  mondayDocV2?: string;
}

const fieldLabels: Record<keyof ColumnMapping, string> = {
  nombre: 'Nombre',
  tareas: 'Tareas',
  responsable: 'Resp.',
  tipoDeOpps: 'Tipo de Opps',
  cliente: 'Cliente',
  etapas: 'Etapas',
  valorDelAcuerdo: 'Valor del acuerdo',
  facturaFinal: 'Factura Final',
  probabilidad: 'Probabilidad',
  ofertaFacturaRemito: 'Oferta/ Factura/ REMITO',
  comprobante: 'Comprobante',
  fechaDeCierre: 'Fecha de Cierre',
  remitoDeEntrega: 'Remito DE ENTREGA',
  condicionDelivery: 'Condicion Delivery',
  estadoDelivery: 'Estado Delivery',
  recibido: 'Recibido',
  infoEntrega: 'Info Entrega',
  pago: 'Pago',
  condicionPago: 'Condicion Pago',
  conciliado: 'Conciliado',
  abono: 'Abono ($)',
  saldo: 'Saldo',
  fechaDeEntrega: 'Fecha de entrega',
  fechaVto: 'Fecha VTO',
  fechaAbono: 'Fecha Abono',
  diasDeAtraso: 'Días de Atraso',
  comision: 'Comisión',
  montoComision: 'Monto Comisión',
  mondayDocV2: 'monday Doc v2',
};

// Monday.com specific: map payment status values
const mapMondayPaymentStatus = (value: string): string => {
  if (!value) return 'Pending';
  const v = value.toLowerCase();
  if (v.includes('pagado') && !v.includes('parcial')) return 'Paid';
  if (v.includes('parcial')) return 'Partial';
  if (v.includes('pendiente') || v.includes('cobro')) return 'Pending';
  if (v.includes('cheque')) return 'Pending'; // Cheque entregado pero no debitado
  if (v.includes('incobrable')) return 'Cancelled';
  return 'Pending';
};

// Monday.com specific: map payment method values
const mapMondayPaymentMethod = (value: string): string => {
  if (!value) return 'Efectivo';
  const v = value.toLowerCase();
  if (v.includes('cheque')) return 'Cheque';
  if (v.includes('cuenta corriente')) return 'CuentaCorriente';
  if (v.includes('contado')) return 'Efectivo';
  if (v.includes('transferencia')) return 'Transferencia';
  return 'Efectivo';
};

// Convert Excel serial date to ISO string
const excelDateToISO = (excelDate: number | string): string | undefined => {
  if (typeof excelDate === 'string') {
    // Already a string, try to parse
    const d = new Date(excelDate);
    if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
    return undefined;
  }
  if (typeof excelDate === 'number' && excelDate > 40000 && excelDate < 60000) {
    // Excel serial date
    const date = new Date((excelDate - 25569) * 86400 * 1000);
    return date.toISOString().split('T')[0];
  }
  return undefined;
};

export default function ImportSalesPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'upload' | 'mapping' | 'preview' | 'importing' | 'result'>('upload');
  const [csvData, setCsvData] = useState<CsvRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [createMissingProducts, setCreateMissingProducts] = useState(false);
  const [createMissingCustomers, setCreateMissingCustomers] = useState(true);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [onlyGanado, setOnlyGanado] = useState(true);
  const [importResult, setImportResult] = useState<ImportMondaySalesResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [authorized, setAuthorized] = useState(false);

  // Check feature flag - usa sales-monday-columns para habilitar el import
  useEffect(() => {
    (async () => {
      try {
        const flags = await api.getFeatureFlags();
        const hasFlag = (flags || []).some(f => f.enabled && f.key.toLowerCase() === 'sales-monday-columns');
        if (!hasFlag) {
          navigate('/sales');
          return;
        }
        setAuthorized(true);
      } catch {
        navigate('/sales');
      }
    })();
  }, [navigate]);

  const processFileData = useCallback((data: any[][], fileName: string) => {
    try {
      if (data.length < 2) {
        setError('El archivo debe tener al menos una fila de encabezados y una de datos.');
        return;
      }

      // Monday.com exports have headers not in the first row - find the header row
      // Header row typically has many columns with text values
      let headerRowIndex = 0;
      for (let i = 0; i < Math.min(10, data.length); i++) {
        const row = data[i];
        if (row && row.length > 5) {
          const nonEmptyCount = row.filter((c: any) => c !== undefined && c !== null && c !== '').length;
          if (nonEmptyCount > 5) {
            headerRowIndex = i;
            break;
          }
        }
      }

      const headerLine = data[headerRowIndex].map((h: any) => String(h || '').trim());
      setHeaders(headerLine.filter(h => h !== ''));

      const rows: CsvRow[] = [];
      for (let i = headerRowIndex + 1; i < data.length; i++) {
        const values = data[i];
        if (!values || values.length === 0) continue;

        const row: CsvRow = {};
        let hasData = false;
        headerLine.forEach((header: string, idx: number) => {
          if (header) {
            const val = values[idx];
            row[header] = val !== undefined && val !== null ? String(val) : '';
            if (val !== undefined && val !== null && val !== '') hasData = true;
          }
        });
        if (hasData) rows.push(row);
      }

      setCsvData(rows);

      // Auto-detect mappings based on common column names
      const autoMapping: ColumnMapping = {};
      const lowerHeaders = headerLine.map((h: string) => h.toLowerCase());

      // Patrones de auto-detección para columnas de Monday.com
      const autoMapPatterns: [keyof ColumnMapping, string[]][] = [
        ['nombre', ['nombre']],
        ['tareas', ['tareas']],
        ['responsable', ['resp.', 'resp', 'responsable']],
        ['tipoDeOpps', ['tipo de opps', 'tipo opps']],
        ['cliente', ['cliente']],
        ['etapas', ['etapas']],
        ['valorDelAcuerdo', ['valor del acuerdo']],
        ['facturaFinal', ['factura final']],
        ['probabilidad', ['probabilidad']],
        ['ofertaFacturaRemito', ['oferta/ factura/ remito', 'oferta/factura/remito']],
        ['comprobante', ['comprobante']],
        ['fechaDeCierre', ['fecha de cierre']],
        ['remitoDeEntrega', ['remito de entrega']],
        ['condicionDelivery', ['condicion delivery', 'condición delivery']],
        ['estadoDelivery', ['estado delivery']],
        ['recibido', ['recibido']],
        ['infoEntrega', ['info entrega']],
        ['pago', ['pago']],
        ['condicionPago', ['condicion pago', 'condición pago']],
        ['conciliado', ['conciliado']],
        ['abono', ['abono ($)', 'abono']],
        ['saldo', ['saldo']],
        ['fechaDeEntrega', ['fecha de entrega']],
        ['fechaVto', ['fecha vto']],
        ['fechaAbono', ['fecha abono']],
        ['diasDeAtraso', ['días de atraso', 'dias de atraso']],
        ['comision', ['comisión', 'comision']],
        ['montoComision', ['monto comisión', 'monto comision']],
        ['mondayDocV2', ['monday doc v2', 'monday doc']],
      ];

      for (const [field, patterns] of autoMapPatterns) {
        const matchIdx = lowerHeaders.findIndex((h: string) => patterns.some(p => h.includes(p)));
        if (matchIdx !== -1) {
          autoMapping[field] = headerLine[matchIdx];
        }
      }

      setMapping(autoMapping);
      setStep('mapping');
    } catch (err) {
      setError('Error al procesar el archivo. Verifique el formato.');
    }
  }, []);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    if (isExcel) {
      // Handle Excel files
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];
          processFileData(jsonData, file.name);
        } catch (err) {
          setError('Error al leer el archivo Excel. Verifique que sea un archivo válido.');
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      // Handle CSV files
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);

          // Parse CSV (handle quoted values)
          const parseCSVLine = (line: string): string[] => {
            const result: string[] = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if ((char === ',' || char === ';') && !inQuotes) {
                result.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            result.push(current.trim());
            return result;
          };

          const jsonData = lines.map(line => parseCSVLine(line));
          processFileData(jsonData, file.name);
        } catch (err) {
          setError('Error al procesar el archivo CSV. Verifique el formato.');
        }
      };
      reader.readAsText(file);
    }
  }, [processFileData]);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      const input = document.createElement('input');
      input.type = 'file';
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      input.files = dataTransfer.files;
      handleFileUpload({ target: input } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [handleFileUpload]);

  const handleMappingChange = (field: keyof ColumnMapping, value: string) => {
    setMapping(prev => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const getMappedRows = (): ImportMondaySalesRow[] => {
    return csvData
      .filter(row => {
        // If etapas column is mapped, apply filters
        if (mapping.etapas) {
          const status = (row[mapping.etapas] || '').toLowerCase().trim();
          // Skip header rows
          if (status === 'etapas') return false;
          // If onlyGanado is enabled, only include "Ganado" rows
          if (onlyGanado && status !== 'ganado') return false;
          // Always skip "Perdido" status
          if (status === 'perdido') return false;
        }
        return true;
      })
      .map(row => {
        // Parse date (handle Excel serial dates)
        const parseDate = (fieldName: string | undefined): string | undefined => {
          if (!fieldName) return undefined;
          const rawDate = row[fieldName];
          if (!rawDate) return undefined;
          const numDate = parseFloat(rawDate);
          if (!isNaN(numDate) && numDate > 40000) {
            return excelDateToISO(numDate);
          }
          return excelDateToISO(rawDate);
        };

        // Parse number (handle currency formats)
        const parseNumber = (val: string | undefined): number | undefined => {
          if (!val) return undefined;
          const cleaned = val.toString().replace(/[^0-9.,\-]/g, '').replace(',', '.');
          const num = parseFloat(cleaned);
          return isNaN(num) ? undefined : num;
        };

        return {
          // Campos principales
          nombre: mapping.nombre ? row[mapping.nombre] : undefined,
          tareas: mapping.tareas ? row[mapping.tareas] : undefined,
          responsable: mapping.responsable ? row[mapping.responsable] : undefined,
          tipoDeOpps: mapping.tipoDeOpps ? row[mapping.tipoDeOpps] : undefined,
          cliente: mapping.cliente ? row[mapping.cliente] : undefined,
          etapas: mapping.etapas ? row[mapping.etapas] : undefined,
          valorDelAcuerdo: parseNumber(mapping.valorDelAcuerdo ? row[mapping.valorDelAcuerdo] : undefined),
          facturaFinal: parseNumber(mapping.facturaFinal ? row[mapping.facturaFinal] : undefined),
          probabilidad: parseNumber(mapping.probabilidad ? row[mapping.probabilidad] : undefined),
          ofertaFacturaRemito: mapping.ofertaFacturaRemito ? row[mapping.ofertaFacturaRemito] : undefined,
          comprobante: mapping.comprobante ? row[mapping.comprobante] : undefined,
          fechaDeCierre: parseDate(mapping.fechaDeCierre),
          remitoDeEntrega: mapping.remitoDeEntrega ? row[mapping.remitoDeEntrega] : undefined,
          condicionDelivery: mapping.condicionDelivery ? row[mapping.condicionDelivery] : undefined,
          estadoDelivery: mapping.estadoDelivery ? row[mapping.estadoDelivery] : undefined,
          recibido: mapping.recibido ? row[mapping.recibido] : undefined,
          infoEntrega: mapping.infoEntrega ? row[mapping.infoEntrega] : undefined,
          pago: mapping.pago ? row[mapping.pago] : undefined,
          condicionPago: mapping.condicionPago ? row[mapping.condicionPago] : undefined,
          conciliado: mapping.conciliado ? row[mapping.conciliado] : undefined,
          abono: parseNumber(mapping.abono ? row[mapping.abono] : undefined),
          saldo: parseNumber(mapping.saldo ? row[mapping.saldo] : undefined),
          fechaDeEntrega: parseDate(mapping.fechaDeEntrega),
          fechaVto: parseDate(mapping.fechaVto),
          fechaAbono: parseDate(mapping.fechaAbono),
          diasDeAtraso: parseNumber(mapping.diasDeAtraso ? row[mapping.diasDeAtraso] : undefined),
          comision: parseNumber(mapping.comision ? row[mapping.comision] : undefined),
          montoComision: parseNumber(mapping.montoComision ? row[mapping.montoComision] : undefined),
          mondayDocV2: mapping.mondayDocV2 ? row[mapping.mondayDocV2] : undefined,
        };
      });
  };

  const handleImport = async () => {
    setImporting(true);
    setError(null);
    setStep('importing');

    try {
      const rows = getMappedRows();
      const result = await api.importMondaySales({
        rows,
        createMissingProducts,
        createMissingCustomers,
        skipDuplicates,
      });
      setImportResult(result);
      setStep('result');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al importar ventas');
      setStep('preview');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setStep('upload');
    setCsvData([]);
    setHeaders([]);
    setMapping({});
    setImportResult(null);
    setError(null);
  };

  if (!authorized) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <LinearProgress sx={{ width: 200 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        Importar Ventas desde Monday.com
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Paso 1: Cargar archivo CSV</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Exporta tus ventas desde Monday.com (Excel o CSV) y cargalas aquí. El sistema detectará automáticamente las columnas del formato Monday.com.
            </Typography>

            <Box
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 6,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 200,
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
              component="label"
            >
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                hidden
                onChange={handleFileUpload}
              />
              <CloudUpload sx={{ fontSize: 64, color: 'text.secondary', mb: 2, display: 'block' }} />
              <Typography variant="h6" color="text.secondary">
                Arrastrá un archivo Excel o CSV aquí
              </Typography>
              <Typography variant="body2" color="text.secondary">
                o hacé clic para seleccionar (.xlsx, .xls, .csv)
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Column Mapping */}
      {step === 'mapping' && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Paso 2: Mapear columnas</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Asociá cada campo con la columna correspondiente del CSV. Los campos marcados con * son recomendados.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
              {(Object.keys(fieldLabels) as (keyof ColumnMapping)[]).map((field) => (
                <TextField
                  key={field}
                  select
                  fullWidth
                  size="small"
                  label={fieldLabels[field]}
                  value={mapping[field] || ''}
                  onChange={(e) => handleMappingChange(field, e.target.value)}
                >
                  <MenuItem value="">
                    <em>No mapear</em>
                  </MenuItem>
                  {headers.map((header) => (
                    <MenuItem key={header} value={header}>{header}</MenuItem>
                  ))}
                </TextField>
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" sx={{ mb: 2 }}>Opciones de importación</Typography>
            <Stack spacing={1} sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={onlyGanado}
                    onChange={(e) => setOnlyGanado(e.target.checked)}
                  />
                }
                label="Solo importar ventas 'Ganado' (Monday.com)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createMissingProducts}
                    onChange={(e) => setCreateMissingProducts(e.target.checked)}
                  />
                }
                label="Crear productos que no existan"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createMissingCustomers}
                    onChange={(e) => setCreateMissingCustomers(e.target.checked)}
                  />
                }
                label="Crear clientes que no existan"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                  />
                }
                label="Omitir duplicados (misma referencia)"
              />
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={resetImport}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={() => setStep('preview')}
                disabled={!mapping.nombre && !mapping.cliente}
              >
                Vista previa
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Preview */}
      {step === 'preview' && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Paso 3: Vista previa</Typography>
              <Chip label={`${csvData.length} filas`} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Verificá que los datos se vean correctos antes de importar.
            </Typography>

            <TableContainer sx={{ maxHeight: 400, mb: 3 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Etapas</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Fecha Cierre</TableCell>
                    <TableCell>Pago</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getMappedRows().slice(0, 50).map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.nombre || '-'}</TableCell>
                      <TableCell>{row.cliente || '-'}</TableCell>
                      <TableCell>{row.tipoDeOpps || '-'}</TableCell>
                      <TableCell>{row.etapas || '-'}</TableCell>
                      <TableCell align="right">${(row.valorDelAcuerdo || row.facturaFinal || 0).toLocaleString()}</TableCell>
                      <TableCell>{row.fechaDeCierre || '-'}</TableCell>
                      <TableCell>{row.pago || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {csvData.length > 50 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Mostrando 50 de {csvData.length} filas
              </Typography>
            )}

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => setStep('mapping')}>
                Volver
              </Button>
              <Button
                variant="contained"
                startIcon={<Upload />}
                onClick={handleImport}
              >
                Importar {csvData.length} ventas
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Importing */}
      {step === 'importing' && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Importando ventas...</Typography>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Esto puede tardar unos segundos dependiendo de la cantidad de datos.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Result */}
      {step === 'result' && importResult && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              {importResult.success ? (
                <Check sx={{ fontSize: 48, color: 'success.main' }} />
              ) : (
                <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />
              )}
              <Box>
                <Typography variant="h6">
                  {importResult.success ? 'Importación completada' : 'Importación con errores'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {importResult.imported} ventas importadas, {importResult.skipped} omitidas
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Card variant="outlined" sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="success.main">{importResult.imported}</Typography>
                <Typography variant="body2" color="text.secondary">Importadas</Typography>
              </Card>
              <Card variant="outlined" sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="warning.main">{importResult.skipped}</Typography>
                <Typography variant="body2" color="text.secondary">Omitidas</Typography>
              </Card>
              <Card variant="outlined" sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="error.main">{importResult.errors.length}</Typography>
                <Typography variant="body2" color="text.secondary">Errores</Typography>
              </Card>
            </Stack>

            {importResult.errors.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Errores:</Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                  {importResult.errors.map((err, idx) => (
                    <Typography key={idx} variant="body2" color="error.main" sx={{ mb: 0.5 }}>
                      {err}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={resetImport}>
                Importar otro archivo
              </Button>
              <Button variant="contained" href="/sales">
                Ver ventas
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
